import { Button } from '@/shared/components/ui/Button';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Activity, Edit2, Loader2, Plus, Trash2, Webhook } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PushDeliveriesPage } from './PushDeliveriesPage';
import { PushEndpointModal } from './PushEndpointModal';
import { type PushEndpoint, deleteEndpoint, listEndpoints, updateEndpoint } from './api';

const PAGE_SIZE = 50;

export function PushPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PushEndpoint | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PushEndpoint | null>(null);
  const [historyEndpointId, setHistoryEndpointId] = useState<number | null>(null);

  const listQ = useInfiniteQuery({
    queryKey: ['push-endpoints'],
    queryFn: ({ pageParam }) =>
      listEndpoints({
        limit: PAGE_SIZE,
        ...(typeof pageParam === 'number' && { cursor: pageParam }),
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });

  const endpoints = useMemo<PushEndpoint[]>(
    () => listQ.data?.pages.flatMap((p) => p.items) ?? [],
    [listQ.data],
  );

  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !listQ.hasNextPage || listQ.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) void listQ.fetchNextPage();
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [listQ.hasNextPage, listQ.isFetchingNextPage, listQ.fetchNextPage]);

  // Mede primeira linha + header pra setar maxHeight = 10 linhas visíveis,
  // independente do conteúdo (badges wrappable, padding etc).
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [scrollMaxHeight, setScrollMaxHeight] = useState<number | null>(null);
  useEffect(() => {
    const t = tableRef.current;
    if (!t || endpoints.length === 0) return;
    const headerRow = t.querySelector('thead tr') as HTMLElement | null;
    const firstBodyRow = t.querySelector('tbody tr') as HTMLElement | null;
    if (!headerRow || !firstBodyRow) return;
    setScrollMaxHeight(headerRow.offsetHeight + firstBodyRow.offsetHeight * 10);
  }, [endpoints.length]);

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => updateEndpoint(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['push-endpoints'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteEndpoint(id),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ['push-endpoints'] });
    },
  });

  return (
    <div className="flex flex-1 flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="font-bold text-[20px] text-n-900" style={{ letterSpacing: '-.02em' }}>
          Webhooks
        </h1>
        <Button onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" />
          Novo webhook
        </Button>
      </header>

      <p className="text-[12px] text-n-500">
        Configure URLs externas que o ChatPlus chama quando eventos acontecem (n8n, Fluent, sistema
        próprio…).
      </p>

      <div
        className="overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm"
        style={scrollMaxHeight ? { maxHeight: scrollMaxHeight } : undefined}
      >
        <table ref={tableRef} className="w-full text-[13px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Nome
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Canal
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                URL
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Eventos
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Status
              </th>
              <th className="px-[14px] py-[10px] text-right font-bold text-[11px] uppercase text-n-500">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center">
                  <Loader2 className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!listQ.isLoading && endpoints.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center text-n-500">
                  Nenhum webhook configurado.
                </td>
              </tr>
            )}
            {endpoints.map((e) => (
              <tr key={e.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px]">
                  <div className="flex items-center gap-2">
                    <Webhook size={14} className="text-sky-500" />
                    <span className="font-semibold text-n-900">{e.name}</span>
                  </div>
                </td>
                <td className="px-[14px] py-[11px] text-n-700">
                  {e.channel_name || e.channel_number || '—'}
                  {e.channel_type && (
                    <span className="ml-1 text-[11px] text-n-400">
                      {e.channel_type === 'meta_official'
                        ? 'Oficial'
                        : e.channel_type === 'instagram'
                          ? 'Instagram'
                          : 'QR'}
                    </span>
                  )}
                </td>
                <td className="px-[14px] py-[11px] text-[11px] text-n-600">
                  {e.url.length > 60 ? `${e.url.slice(0, 60)}…` : e.url}
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex flex-wrap gap-1">
                    {e.events.map((ev) => (
                      <span
                        key={ev}
                        className="rounded-pill bg-n-100 px-2 py-[2px] font-mono text-[10px] text-n-700"
                      >
                        {ev}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-[14px] py-[11px]">
                  <button
                    type="button"
                    onClick={() => toggleMut.mutate({ id: e.id, active: !e.active })}
                    className={`relative h-[20px] w-[34px] cursor-pointer rounded-pill ${
                      e.active ? 'bg-success-500' : 'bg-n-300'
                    }`}
                    aria-label={e.active ? 'Desativar' : 'Ativar'}
                  >
                    <span
                      className={`absolute top-[2px] h-[16px] w-[16px] rounded-full bg-n-0 shadow ${
                        e.active ? 'right-[2px]' : 'left-[2px]'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => setHistoryEndpointId(e.id)}
                      className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:bg-n-100"
                      title="Histórico de entregas"
                    >
                      <Activity size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(e)}
                      className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:bg-n-100"
                      title="Editar"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(e)}
                      className="flex h-8 w-8 items-center justify-center rounded text-danger-500 hover:bg-danger-50"
                      title="Deletar"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {listQ.hasNextPage && (
              <tr ref={sentinelRef}>
                <td colSpan={6} className="py-4 text-center text-[12px] text-n-500">
                  {listQ.isFetchingNextPage ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Carregando mais…
                    </span>
                  ) : (
                    'Role para carregar mais'
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && <PushEndpointModal onClose={() => setCreating(false)} />}
      {editing && <PushEndpointModal endpoint={editing} onClose={() => setEditing(null)} />}

      {historyEndpointId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setHistoryEndpointId(null);
          }}
        >
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-y-auto rounded-md bg-n-0 p-6 shadow-xl">
            <PushDeliveriesPage
              endpointId={historyEndpointId}
              onClose={() => setHistoryEndpointId(null)}
            />
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmDelete(null);
          }}
        >
          <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-xl">
            <h3 className="font-bold text-[16px] text-n-900">Apagar webhook?</h3>
            <p className="mt-2 text-[13px] text-n-600">
              &quot;{confirmDelete.name}&quot; será removido. Histórico de entregas será apagado em
              cascata.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="rounded border border-n-200 bg-n-0 px-3 py-2 font-semibold text-[13px]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => deleteMut.mutate(confirmDelete.id)}
                className="rounded bg-danger-500 px-3 py-2 font-semibold text-[13px] text-white"
              >
                Apagar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
